import { FC, useEffect, useState } from 'react';
import { FacebookIntegrationsContext } from './FacebookIntegrationContext';
import { useGetFacebookIntegration } from '../hooks/useGetFacebookIntegrations';
import { useDeleteFacebookIntegration } from '../hooks/useDeleteFacebookIntegration';
import { useSubmitAdAccounts } from '../hooks/useSubmitAdAccounts';

export const FacebookIntegrationsContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const {
    data: facebookIntegrations = [],
    isLoading: isGettingIntegrations,
    isError: hasIntegrationsError,
    isSuccess: hasReceivedIntegrations,
    refetch: refetchIntegrations,
  } = useGetFacebookIntegration();

  const { isSuccess: hasSubmittedAdAccount, isLoading: isSubmitting } = useSubmitAdAccounts();
  const {
    id: selectedId,
    targetId,
    isLoading: isUnsubscribing,
    isError: isNotUnsubscribed,
    isSuccess: isUnsubscribed,
    unsubscribe,
  } = useDeleteFacebookIntegration();

  const [integrations, updateIntegrations] = useState(facebookIntegrations);

  const removeFacebookIntegration = (id: number) => {
    unsubscribe(id);
    updateIntegrations(integrations.filter(({ config }) => config.id !== id));
  };

  useEffect(() => {
    if (!isGettingIntegrations && hasReceivedIntegrations) {
      refetchIntegrations();
      updateIntegrations(facebookIntegrations);
    }
  }, [
    isGettingIntegrations,
    hasReceivedIntegrations,
    isSubmitting,
    hasSubmittedAdAccount,
    facebookIntegrations,
    facebookIntegrations.length > 0,
  ]);

  const extendedFacebookIntegrations = integrations?.map((integration) => ({
    ...integration,
    filterText: `${integration.config.id} ${integration.config.adAccountId}`,
  }));

  const value = {
    facebookIntegrations: extendedFacebookIntegrations,
    removeFacebookIntegration,
    isGettingIntegrations,
    hasIntegrationsError,
    hasReceivedIntegrations,
    selectedId,
    targetId,
    refetchIntegrations,
    isUnsubscribed,
    isNotUnsubscribed,
    isUnsubscribing,
    unsubscribe,
  };

  return (
    <FacebookIntegrationsContext.Provider value={value}>
      {children}
    </FacebookIntegrationsContext.Provider>
  );
};
